// ────────────────────────────────────────────────────────────────────────────
// Cockpit booking action.
//
// Turns a To-Book row + its selected sailing into a real booking the same way
// the order's task screen does: the pre-save checks, the TMS booking with its
// paired BookingDeparture / BookingArrival tasks, then the carrier through
// My.Ferry when the crossing is linked and the Integration switch is on, then
// the carrier's answer written back onto the TMS row. A crossing that is not
// booked through the ferry service is a plain TMS booking — nothing here
// blocks one on the ferry service's account.
// ────────────────────────────────────────────────────────────────────────────

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { showToast, type ToastType } from '@my/ui';
import {
  useCreateLocationAddress,
  useLocations,
  useOrganisationCarriers,
} from '@my/directory-api/hooks';
import type { Address } from '@my/directory-api';
import {
  useCreateOrderBooking,
  useOrderTasks,
  useResequenceOrderTasks,
  type BookingTypeValue,
  type OrderTask,
} from '@my/tms-api';
import { ferryBlockingMessage, useFerryBookingFlow } from '../../ferry/flow';
import { utcToZoned } from '../../ferry/portTime';
import { useCrossingCatalogue } from '../../ferry/useCrossingCatalogue';
import { bookingFieldsFrom, EMPTY_BOOKING_FORM_VALUES } from '../../orders/lib/bookingForm';
import {
  buildPortAddressCode,
  matchAddressByCode,
  matchAddressForPort,
} from '../../orders/lib/route';
import type { BookingFormValues } from '../../orders/lib/types';
import { isoDateFromAbsMinutes, type SailingRow } from './bookingDetail';
import type { BookingRow } from './mockData';

/** Delivery-side task types — the ferry block slots in just before the first one. */
const DELIVERY_TYPES = new Set(['Deliver', 'DropOff', 'Unload']);

/** What the panel adds to a selected sailing: the unit as the order has it, and the operator's switches. */
export interface CockpitBookingInput {
  bookingType: BookingTypeValue;
  isAccompanied: boolean;
  numberOfDrivers: string;
  vehicleRegistration: string;
  trailerNumber: string;
  ferryBook: boolean;
  /** The carrier account picked when the organisation holds more than one. */
  credentialId: string;
  exportId: string;
  importId: string;
}

export function useCockpitBooking(orgId: string | null, orderId: string) {
  const qc = useQueryClient();
  const { carrierByKey } = useCrossingCatalogue();
  const orgCarriersQuery = useOrganisationCarriers(orgId);
  const locationsQuery = useLocations(orgId);
  const tasksQuery = useOrderTasks(orderId);
  const createLocation = useCreateLocationAddress();
  const createBooking = useCreateOrderBooking();
  const resequence = useResequenceOrderTasks();
  const ferryFlow = useFerryBookingFlow();

  const [isBooking, setIsBooking] = useState(false);

  // A carrier row is registered under both its key styles, as the order form
  // does, so a route picked from Ferry's list finds the party of a carrier
  // saved under the file's key.
  const partyIdByCarrierKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of orgCarriersQuery.data ?? []) {
      if (!c.partyId) continue;
      map.set(c.catalogueKey, c.partyId);
      const alias = carrierByKey.get(c.catalogueKey)?.linkedKey;
      if (alias && !map.has(alias)) map.set(alias, c.partyId);
    }
    return map;
  }, [orgCarriersQuery.data, carrierByKey]);

  /**
   * Book the selected sailing for a row. Returns true on success so the caller
   * can collapse the panel; surfaces all failures as toasts.
   */
  async function bookRow(
    row: BookingRow,
    selected: SailingRow,
    input: CockpitBookingInput,
  ): Promise<boolean> {
    if (!orgId) {
      showToast({ type: 'error', message: 'No organisation selected.' });
      return false;
    }
    const candidate = selected.candidate;
    if (!candidate) {
      showToast({ type: 'error', message: "This sailing is not one of the organisation's routes." });
      return false;
    }
    if (!tasksQuery.data) {
      showToast({ type: 'error', message: 'Order tasks are still loading — try again in a moment.' });
      return false;
    }
    setIsBooking(true);
    try {
      const { entry, carrier, reversed, orgRouteId } = candidate;
      const originName = reversed ? entry.destinationPortName : entry.originPortName;
      const originCountry = reversed ? entry.destinationCountryIsoCode : entry.originCountryIsoCode;
      const originCode = reversed ? entry.destinationPortCode : entry.originPortCode;
      const destName = reversed ? entry.originPortName : entry.destinationPortName;
      const destCountry = reversed ? entry.originCountryIsoCode : entry.destinationCountryIsoCode;
      const destCode = reversed ? entry.originPortCode : entry.destinationPortCode;

      // 1. Each port to a Location address: the carrier's terminal by its code
      //    (the order form's rows), else by name, else a new coded row.
      const locations: readonly Address[] = locationsQuery.data ?? [];
      const resolvePort = async (
        name: string,
        country: string,
        portCode: string | null | undefined,
      ): Promise<string> => {
        const code = buildPortAddressCode(carrier?.code ?? null, portCode);
        const byCode = code ? matchAddressByCode(locations, code) : null;
        if (byCode) return byCode.id;
        const byName = matchAddressForPort(locations, name, country);
        if (byName) return byName.id;
        return createLocation.mutateAsync({
          organisationId: orgId,
          addressType: 'Location',
          name,
          code: code ?? undefined,
          countryIsoCode: country,
        });
      };
      const departureAddressId = await resolvePort(originName, originCountry, originCode);
      const arrivalAddressId = await resolvePort(destName, destCountry, destCode);

      // 2. The form values the shared flow expects. A real sailing's dates and
      //    times are its own, in each port's clock. A sample or time-less row
      //    books on the delivery day with no time; the operator sets the
      //    sailing on the order afterwards.
      const deliveryDay = isoDateFromAbsMinutes(Math.max(0, row.deliveryDayOffset) * 1440);
      const departure = selected.ferry
        ? utcToZoned(selected.ferry.sailingUtc, selected.ferry.originZone)
        : { date: deliveryDay, time: '' };
      const arrival = selected.ferry?.arrivalUtc
        ? utcToZoned(selected.ferry.arrivalUtc, selected.ferry.destinationZone)
        : { date: departure.date, time: '' };
      const sailDate = departure.date;
      const sailTime = departure.time;
      const ferry = input.ferryBook ? selected.ferry : null;
      const values: BookingFormValues = {
        ...EMPTY_BOOKING_FORM_VALUES,
        bookingType: input.bookingType,
        isAccompanied: input.isAccompanied,
        numberOfDrivers: input.numberOfDrivers,
        vehicleRegistration: input.vehicleRegistration,
        trailerNumber: input.trailerNumber,
        sailDate,
        sailTime,
        departureAddressId,
        arrivalAddressId,
        carrierPartyId: partyIdByCarrierKey.get(entry.carrierKey) ?? '',
        carrierRouteId: orgRouteId,
        routeCode: entry.code ?? '',
        routeName: entry.name,
        typicalDurationMinutes: entry.typicalDurationMinutes ?? null,
        typicalCheckInMinutes: entry.typicalCheckInMinutes ?? null,
        typicalDriveOffMinutes: entry.typicalDriveOffMinutes ?? null,
        ferryBook: input.ferryBook,
        ...(ferry
          ? {
              ferryCarrierId: ferry.carrierId,
              ferryRouteCode: ferry.routeCode,
              ferryOriginZone: ferry.originZone,
              ferryCredentialRequired: ferry.credentialRequired,
              ferryCredentialId: ferry.credentials.some((c) => c.id === input.credentialId)
                ? input.credentialId
                : ferry.credentialId,
              ferrySailingUtc: ferry.sailingUtc,
              ferryVehicleTypeCode: ferry.vehicleTypeCode,
              ferryLengthMetres: ferry.lengthMetres,
              ferryDangerousGoods: row.hazardous ?? false,
              ferryPlugIn: row.temperatureC != null,
              ferryPluginTemperature: row.temperatureC != null ? String(row.temperatureC) : '',
              ferryExportId: input.exportId.trim().toUpperCase(),
              ferryImportId: input.importId.trim().toUpperCase(),
            }
          : {}),
      };

      // 3. A Ferry-linked crossing is checked before anything is created.
      const blocking = ferryBlockingMessage(values);
      if (blocking) {
        showToast({ type: 'error', message: blocking });
        return false;
      }

      // 4. Sequence the ferry legs INTO the existing task list — right before
      //    the first delivery so it reads Collect → Board → Disembark → Deliver.
      //    Appends when the order has no delivery task yet.
      const tasks = [...tasksQuery.data].sort((a, b) => a.sequence - b.sequence);
      const firstDelivery = tasks.find((t) => DELIVERY_TYPES.has(t.type));
      let boardingSeq: number;
      let disembarkSeq: number;
      let toBump: OrderTask[] = [];
      if (firstDelivery) {
        boardingSeq = firstDelivery.sequence;
        disembarkSeq = boardingSeq + 1;
        toBump = tasks.filter((t) => t.sequence >= firstDelivery.sequence);
      } else {
        const maxSeq = tasks.reduce((m, t) => Math.max(m, t.sequence), 0);
        boardingSeq = maxSeq + 1;
        disembarkSeq = maxSeq + 2;
      }

      // 5. The TMS booking + its two half tasks.
      const created = await createBooking.mutateAsync({
        orderId: row.id,
        ...bookingFieldsFrom(values),
        departure: {
          addressId: departureAddressId,
          sequence: boardingSeq,
          plannedDate: sailDate,
          plannedTime: sailTime || null,
          plannedDepartureTime: sailTime || null,
        },
        arrival: {
          addressId: arrivalAddressId,
          sequence: disembarkSeq,
          plannedDate: arrival.date,
          plannedTime: arrival.time || null,
        },
      });

      // 6. Then the carrier, when the crossing is booked through My.Ferry. A
      //    failure there undid the TMS booking, so nothing is left to renumber.
      let tone: ToastType = 'success';
      let message = `Booked ${selected.routeLabel} for ${row.orderNumber}.`;
      if (values.ferryRouteCode && values.ferryCarrierId) {
        const sent = await ferryFlow.sendToFerry({
          orderId: row.id,
          tmsBooking: created,
          values,
          orderNumber: row.orderNumber,
        });
        if (sent.outcome === 'failed') {
          showToast({ type: 'error', message: sent.message });
          return false;
        }
        if (sent.outcome === 'unknown') {
          tone = 'warning';
          message = sent.message;
        } else if (sent.outcome === 'refused') {
          tone = 'error';
          message = `The carrier refused the booking: ${sent.message}`;
        } else {
          message = `Booked with the carrier, reference ${sent.booking.carrierBookingNumber}.`;
        }
      }

      // 7. Shift the delivery-side tasks down by 2 so the ferry block sits
      //    between collection and delivery, in one call for the whole order.
      if (toBump.length > 0) {
        const bumped = new Set(toBump.map((t) => t.id));
        await resequence.mutateAsync({
          orderId: row.id,
          items: [
            ...tasks.map((t) => ({
              taskId: t.id,
              sequence: bumped.has(t.id) ? t.sequence + 2 : t.sequence,
            })),
            ...created.tasks.map((t) => ({ taskId: t.id, sequence: t.sequence })),
          ],
        });
      }

      // 8. Refresh the cockpit worklist (prefix-invalidate all booking caches)
      //    so the row jumps To-Book → Booked.
      qc.invalidateQueries({ queryKey: ['tms', 'orderBooking'] });
      showToast({ type: tone, message });
      return true;
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to create the booking.',
      });
      return false;
    } finally {
      setIsBooking(false);
    }
  }

  return { bookRow, isBooking };
}
